import useSWR, { mutate } from 'swr';
import api from '../../services/api';

export interface WorkflowRule {
  id?: number;
  trigger: string;
  entityType?: string; // Entity type this rule applies to (Task, Ticket, Client, etc.)
  conditions: any;
  actions: any;
  order?: number;
}

export interface Workflow {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  rules: WorkflowRule[];
  createdAt: string;
  updatedAt: string;
}

export interface TestWorkflowData {
  entityType: string;
  entityId: number;
  trigger: string;
  entityData?: any;
}

// Fetch all workflows
export const useWorkflows = (includeInactive = false) => {
  const { data, error, isLoading, mutate } = useSWR<Workflow[]>(
    `/workflows?includeInactive=${includeInactive}`,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Fetch a single workflow
export const useWorkflow = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Workflow>(
    id ? `/workflows/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    client: data, // GenericFormPage expects either data or client
    isLoading,
    error,
    mutate,
  };
};

// Create a new workflow
export const createWorkflow = async (workflowData: Partial<Workflow>): Promise<Workflow> => {
  const response = await api.post('/workflows', workflowData);
  await mutate('/workflows');
  return response.data;
};

// Update a workflow
export const updateWorkflow = async (id: number, workflowData: Partial<Workflow>): Promise<Workflow> => {
  const response = await api.patch(`/workflows/${id}`, workflowData);
  await mutate(`/workflows/${id}`);
  await mutate('/workflows');
  return response.data;
};

// Delete a workflow
export const deleteWorkflow = async (id: number): Promise<void> => {
  await api.delete(`/workflows/${id}`);
  await mutate('/workflows');
};

// Test a workflow
export const testWorkflow = async (id: number, testData: TestWorkflowData): Promise<any> => {
  const response = await api.post(`/workflows/${id}/test`, testData);
  return response.data;
};

// Execute workflows for an entity
export const executeWorkflows = async (
  entityType: string,
  entityId: number,
  trigger: string,
  entityData?: any
): Promise<any> => {
  const response = await api.post('/workflows/execute', {
    entityType,
    entityId,
    trigger,
    entityData,
  });
  return response.data;
};
